import React from 'react';
import { Box, Button, Chip, Divider, Grid, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ModalLayout from 'commons/components/ModalLayout';
import { getQuoteStateLabel } from 'utils/quoteCalculations';
import { lsParams, QUOTE_STATE } from 'utils/enums';

const STATE_CHIP_COLOR = {
  [QUOTE_STATE.PENDING]: 'default',
  [QUOTE_STATE.AWARDED]: 'success',
  [QUOTE_STATE.LOST]: 'error',
};

const SummaryField = React.memo(({ label, children }) => (
  <Grid item xs={6} sm={3}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography>{children}</Typography>
  </Grid>
));

const HistoricViewDetailsModal = function (props) {
  const { open, toggleModalFnc, data, actions } = props;
  const {
    name,
    cMaterial,
    quoteId,
    cost,
    zcotCost,
    formulaVersionId,
    formulaVersionLabel,
    margin,
    details,
    subtotal,
    // quoteState,
    quoteMaterialState,
  } = data;
  // `formulaVersionId` siempre existe (toda cotización congela una foto de la fórmula), así
  // que si se corrigió el costo se determina comparando lo usado contra el ZCOT de ese momento,
  // no por la sola presencia de una versión asociada.
  const costsDiffer = Number(zcotCost) !== 0 && Number(cost) !== Number(zcotCost);
  const diffAmount = costsDiffer ? Number(cost) - Number(zcotCost) : 0;
  const diffPercent = costsDiffer ? (diffAmount / Number(zcotCost)) * 100 : 0;
  const diffSign = diffAmount > 0 ? '+' : '';

  const handleViewFormula = () => {
    actions.getFormulaVersionDetail(formulaVersionId);
    actions.changeModals('formulaDetail');
  };

  return (
    <ModalLayout
      width="800px"
      maxWidth="800px"
      minHeight="400px"
      open={open}
      toggleModalFnc={toggleModalFnc}>
      <Grid container mb="4px" justifyContent="space-between" alignItems="flex-start">
        <Grid item>
          <Typography variant="h4">{`${cMaterial} - ${name}`}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`Cotización #${quoteId}`}
          </Typography>
        </Grid>
        <Grid item display="flex" alignItems="center" gap="12px">
          <Chip
            size="small"
            color={STATE_CHIP_COLOR[quoteMaterialState] || 'default'}
            label={getQuoteStateLabel(quoteMaterialState)}
          />
          <IconButton onClick={toggleModalFnc} size="small">
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Divider sx={{ my: '20px' }} />

      {/* Bloque económico: agrupa todo lo relacionado al costo/precio de la línea. */}
      <Box
        mb="24px"
        p="16px"
        sx={{
          border: '1px solid',
          borderColor: costsDiffer ? 'warning.main' : 'divider',
          borderRadius: 1,
        }}>
        <Grid container justifyContent="space-between" alignItems="flex-start">
          <Typography variant="overline" color="text.secondary">
            Costo aplicado a la cotización
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!formulaVersionId}
            onClick={handleViewFormula}>
            Ver detalle de fórmula
          </Button>
        </Grid>
        <Grid container alignItems="center" spacing={1.5} mb="4px">
          <Grid item>
            <Typography variant="h5" fontWeight="bold">
              {cost ? cost.toLocaleString('es-CL', lsParams) : '-'}
            </Typography>
          </Grid>
          <Grid item>
            <Chip
              size="small"
              variant={costsDiffer ? 'filled' : 'outlined'}
              color={costsDiffer ? 'warning' : 'default'}
              label={costsDiffer ? `ZCOR · versión ${formulaVersionLabel}` : 'ZCOT vigente'}
            />
          </Grid>
        </Grid>
        {costsDiffer && (
          <Typography variant="body2" color="warning.main" mb="12px">
            {`ZCOT vigente en ese momento: ${Number(zcotCost).toLocaleString(
              'es-CL',
              lsParams,
            )} (diferencia ${diffSign}${diffAmount.toLocaleString(
              'es-CL',
              lsParams,
            )}, ${diffSign}${diffPercent.toLocaleString('es-CL', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })}%)`}
          </Typography>
        )}
        <Divider sx={{ my: '12px' }} />
        <Grid container spacing={2}>
          <SummaryField label="Margen">{`${margin}%`}</SummaryField>
          <SummaryField label="Total venta">
            {subtotal ? subtotal?.toLocaleString('es-CL', lsParams) : '-'}
          </SummaryField>
        </Grid>
      </Box>

      <Grid container>
        <Grid item xs={12}>
          <Typography fontWeight="bold" mb="4px">
            Comentarios
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={{ whiteSpace: 'pre-line' }}>{`${details}` || '-'}</Typography>
        </Grid>
      </Grid>
    </ModalLayout>
  );
};

export default HistoricViewDetailsModal;
